import { FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDemoContext } from '../DemoContext'
import { ArrowLeft, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type FormState = {
  session_name: string
  test_date: string
  description: string
  class_id: string
  max_points: string
}

const today = new Date().toISOString().slice(0, 10)

export default function DemoCreateExam() {
  const navigate = useNavigate()
  const { classes, addExam } = useDemoContext()
  const { t } = useTranslation('pages')

  const [form, setForm] = useState<FormState>({
    session_name: '',
    test_date: today,
    description: '',
    class_id: classes.length > 0 ? String(classes[0].id) : '',
    max_points: '',
  })

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    const newId = Date.now()
    addExam({
      id: newId,
      session_name: form.session_name,
      test_date: form.test_date,
      class_id: form.class_id ? Number(form.class_id) : null,
      created_at: new Date().toISOString(),
    })
    navigate(`/demo/exams/${newId}`)
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-16 mb-24">
        <button className="btn btn-ghost" onClick={() => navigate('/demo/exams')}>
          <ArrowLeft size={16} />
          {t('common.back')}
        </button>
        <div>
          <h2 className="section-title">{t('demo.createExam.title')}</h2>
          <p className="text-sm text-muted mt-4">{t('demo.createExam.subtitle')}</p>
        </div>
      </div>

      <form onSubmit={onSubmit} style={{ maxWidth: 720 }}>
        <div className="card card-padding mb-24">
          <h3 className="card-title mb-20">{t('demo.createExam.infoCard')}</h3>

          <div className="flex flex-col gap-16">
            <div className="form-group">
              <label className="form-label">{t('demo.createExam.nameLabel')}</label>
              <input
                type="text"
                className="form-input"
                placeholder={t('demo.createExam.namePlaceholder')}
                required
                value={form.session_name}
                onChange={(e) => update('session_name', e.target.value)}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('demo.createExam.dateLabel')}</label>
                <input
                  type="date"
                  className="form-input"
                  required
                  value={form.test_date}
                  onChange={(e) => update('test_date', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('demo.createExam.maxPointsLabel')}</label>
                <input
                  type="number"
                  className="form-input"
                  min={1}
                  required
                  placeholder={t('demo.createExam.maxPointsPlaceholder')}
                  value={form.max_points}
                  onChange={(e) => update('max_points', e.target.value)}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('demo.createExam.classLabel')}</label>
                <select
                  className="form-select"
                  required
                  value={form.class_id}
                  onChange={(e) => update('class_id', e.target.value)}
                >
                  {classes.length === 0 && <option value="">{t('demo.createExam.classNotFound')}</option>}
                  {classes.map((cls) => (
                    <option key={cls.id} value={String(cls.id)}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('demo.createExam.descLabel')}</label>
              <textarea
                className="form-input"
                rows={3}
                placeholder={t('demo.createExam.descPlaceholder')}
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* File uploads — demo no-op */}
        <div className="card card-padding mb-24">
          <h3 className="card-title mb-20">{t('demo.createExam.filesCard')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('demo.createExam.rubricLabel')}</label>
              <div
                style={{
                  border: '2px dashed var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  padding: '24px 16px',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  cursor: 'default',
                  opacity: 0.6,
                }}
              >
                <Upload size={24} style={{ marginBottom: 8, opacity: 0.4 }} />
                <p className="text-sm">{t('common.uploadDisabledDemo')}</p>
                <p className="text-xs text-muted mt-4">PDF, DOCX, JPG, PNG</p>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('demo.createExam.questionPaperLabel')}</label>
              <div
                style={{
                  border: '2px dashed var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  padding: '24px 16px',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  cursor: 'default',
                  opacity: 0.6,
                }}
              >
                <Upload size={24} style={{ marginBottom: 8, opacity: 0.4 }} />
                <p className="text-sm">{t('common.uploadDisabledDemo')}</p>
                <p className="text-xs text-muted mt-4">PDF, DOCX, JPG, PNG</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-12">
          <button className="btn btn-primary" type="submit">
            {t('demo.createExam.submit')}
          </button>
          <button className="btn btn-outline" type="button" onClick={() => navigate('/demo/exams')}>
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </div>
  )
}
